/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import * as sha512 from 'js-sha512';
import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { Observable, Subscriber } from 'rxjs';

/* eslint-disable */
declare var openpgp: any;

/* eslint-enable */

@Injectable()
export class PGPService {

    constructor(
        private logService: LogService
    ) {
    }

    /**
     * SHA a password.
     * @param password
     */
    public shaPassword(password: string): string {
        return sha512.sha512(password);
    }

    /**
     * Get hash remainder.
     * @param hashPassword
     */
    public getHashRemainder(hashPassword: string): string {
        return hashPassword.substring(32, 48);
    }

    /**
     * Hash a user's password.
     * @param password
     */
    public hashPassword(password: string): string {
        return this.getHashRemainder(this.shaPassword(password));
    }

    /**
     * Get new user's keys.
     * @param userData
     * @param password
     */
    public generateNewUserKeys(userData: any, password: string): Observable<any> {
        return new Observable<any>((observer: Subscriber<any>): void => {
            // Encode their password
            const hashPassword = this.shaPassword(password);
            // Define hash remainder
            const hashRemainder = this.getHashRemainder(hashPassword);
            this.logService.debug(
                'PGPService:generateNewUserKeys()',
                'user password locally encoded',
                { password: hashRemainder }
            );

            this.generateNewKeyPair(userData, hashPassword)
                .subscribe({
                    next: (armoredKeys: any): void => {
                        observer.next({ armoredKeys, hashRemainder });
                        observer.complete();
                    },
                    error: (error: any) => {
                        this.logService.error(
                            'PGPService:generateNewKeyPair()', 'Issue generating new key pair', error
                        );
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Helper function to convert GenerateECCKey promise to observable.
     * @param userData
     * @param userKey
     */
    public generateNewKeyPair(userData: object, userKey: string): Observable<any> {
        return new Observable<any>((observer: Subscriber<any>): void => {
            this.GenerateECCKey('ecc', 'ed25519', [userData], userKey, 'armored')
                .then(armoredKeys => {
                    observer.next(armoredKeys);
                    observer.complete();
                })
                .catch(error => {
                    observer.error(error);
                    observer.complete();
                });
        });
    }

    /**
     * Get decrypted org key using the user's password.
     * @param userPassword
     * @param userPrivateKey
     * @param orgPrivateKey
     */
    public getDecryptedOrgKeyWithUserPassword(userPassword: string, userPrivateKey: string, orgPrivateKey: string): Observable<any> {
        return new Observable<any>((observer: Subscriber<any>): void => {
            this.decryptPrivateKeyWithPasswordPromise(userPassword, userPrivateKey, true)
                .then(decryptedUserKey =>
                    this.decryptPrivateKeyWithPasswordPromise(decryptedUserKey.armor(), orgPrivateKey, false))
                .then(decryptedOrgKey => {
                    observer.next(decryptedOrgKey);
                    observer.complete();
                })
                .catch(error => {
                    observer.error(error);
                    observer.complete();
                });
        });
    }

    /**
     * Encrypt key with password (without changing original key).
     * @param password
     * @param privateKeyArmored
     */
    public encryptKeyWithPassword(password: string, privateKeyArmored: string): Observable<any> {
        return new Observable<any>((observer: Subscriber<any>): void => {
            this.encryptKeyWithPasswordPromise(password, privateKeyArmored)
                .then(privateKey => {
                    observer.next(privateKey);
                    observer.complete();
                })
                .catch(error => {
                    observer.error(error);
                    observer.complete();
                });
        });
    }

    /**
     * Helper function to convert decrypt promise to observable.
     * @param password
     * @param privateKeyArmored
     * @param sha512Key
     */
    public decryptPrivateKeyWithPassword(password: string, privateKeyArmored: string, sha512Key?: boolean): Observable<any> {
        return new Observable<any>((observer: Subscriber<any>): void => {
            this.decryptPrivateKeyWithPasswordPromise(password, privateKeyArmored, sha512Key)
                .then(keys => {
                    observer.next(keys);
                    observer.complete();
                })
                .catch(error => {
                    observer.error(error);
                    observer.complete();
                });
        });
    }

    /**
     * Helper function to convert decrypt file promise to observable.
     * @param armoredPrivateKey
     * @param encryptedFile
     */
    public decryptFileWithKey(armoredPrivateKey: any, encryptedFile: any): Observable<any> {
        return new Observable<any>((observer: Subscriber<any>): void => {
            this.decryptFileWithKeyPromise(armoredPrivateKey, encryptedFile)
                .then(decryptedFile => {
                    observer.next(decryptedFile);
                    observer.complete();
                })
                .catch(error => {
                    observer.error(error);
                    observer.complete();
                });
        });
    }

    /**
     * Create signed message for validation purposes.
     * @param message
     * @param privateKey
     */
    public signClearMessageWithPrivateKey(message: string, privateKey: string | ArrayBuffer): Observable<any> {
        return new Observable<any>((observer: Subscriber<any>): void => {
            this.signClearMessageWithPrivateKeyPromise(message, privateKey)
                .then(signedMessage => {
                    observer.next(signedMessage);
                    observer.complete();
                })
                .catch(error => {
                    observer.error(error);
                    observer.complete();
                });
        });
    }

    /**
     * Generate a new ECC key
     * @param type
     * @param curve
     * @param userIDs
     * @param passphrase
     * @param format
     * @constructor
     * @private
     */
    private async GenerateECCKey(type: string, curve: string, userIDs: object, passphrase: string, format: string): Promise<any> {
        this.logService.debug(
            'PGPService:GenerateECCKey()',
            'Generating new ' + type + ' key using curve ' + curve + ' ' + format,
            userIDs
        );

        return await openpgp.generateKey({ type, curve, userIDs, passphrase, format });
    }

    /**
     * Encrypt a key with the user's password.
     * @param password
     * @param privateKeyArmored
     * @private
     */
    private async encryptKeyWithPasswordPromise(password: string, privateKeyArmored: string): Promise<any> {
        return await openpgp.encryptKey(
            {
                privateKey: privateKeyArmored,
                passphrase: password
            }
        );
    }

    /**
     * Decrypts armored key using the user's password.
     * @param password
     * @param privateKeyArmored
     * @param sha512Key
     * @private
     */
    public async decryptPrivateKeyWithPasswordPromise(
        password: string, privateKeyArmored: string, sha512Key: boolean = true
    ): Promise<any> {
        const passphrase: string = sha512Key ? sha512.sha512(password) : password;

        return await openpgp.decryptKey({
            privateKey: await openpgp.readKey({ armoredKey: privateKeyArmored }),
            passphrase
        });
    }

    /**
     * Decrypt file with key.
     * @param armoredPrivateKey
     * @param encryptedFile
     */
    public async decryptFileWithKeyPromise(armoredPrivateKey: any, encryptedFile: any): Promise<string> {
        const encryptedMessage: any = await openpgp.readMessage({
            binaryMessage: encryptedFile
        });

        // I think this is a bug warning.
        // @ts-ignore
        const { data: decrypted } = await openpgp.decrypt({
            message: encryptedMessage,
            decryptionKeys: armoredPrivateKey,
            format: 'binary'
        });

        return decrypted;
    }

    /**
     * Sign a message with a private key.
     * @param message
     * @param privateKey
     */
    public async signClearMessageWithPrivateKeyPromise(message: any, privateKey: any): Promise<string> {
        const unArmoredKey: any = await openpgp.readPrivateKey({ armoredKey: privateKey });
        const unsignedMessage: any = await openpgp.createMessage({ text: message });

        const detachedSignature: any = await openpgp.sign({
            message: unsignedMessage,
            signingKeys: unArmoredKey,
            detached: true
        });

        return detachedSignature;
    }

    /**
     * Read a key to make it usable.
     * @param key
     */
    public readKey(key: any): Observable<any> {
        return new Observable<any>((observer: Subscriber<any>): void => {
            this.readKeyPromise(key)
                .then(key => {
                    observer.next(key);
                    observer.complete();
                })
                .catch((error: any): void => observer.error(error));
        });
    }

    /**
     * Read a key promise.
     * @param privateKeyArmored
     */
    public async readKeyPromise(privateKeyArmored: any): Promise<string> {
        return await openpgp.readPrivateKey({ armoredKey: privateKeyArmored });
    }
}
